import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

import { MessageService } from 'primeng/api';
import { Observable, Subscription, BehaviorSubject, Subject, of } from 'rxjs';
import { take, filter, takeUntil, catchError, switchMap, map } from 'rxjs/operators';

import { TabComponent } from '../../../Common/Layout/components/Tab/tab.component';
import { BrandIdentity } from '../../../Common/BrandIdentity/models/brand-identity';
import { BrandIdentityService } from '../../../Common/BrandIdentity/services/brand-identity.service';
import {
    ManageBrandIdentitiesDialogComponent
} from '../../../Common/BrandIdentity/components/ManageBrandIdentitiesDialog/manage-brand-identities-dialog.component';
import { AppearCondition, AutocompleteCustomOption } from '../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { htmlToFormattedText } from '../../../Common/Misc/helpers/html/html.helper';
import { copy } from '../../../Common/Misc/helpers/copy/copy.helper';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { promptLanguageOptions } from '../../../Common/Forms/data/prompt-language-options';
import { languageDataLocalizationMap } from '../../../Core/Localization/language-localization.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { ViInsightPrompt } from '../../models/vi-insight-prompt.model';
import { parseTextToHtml } from '../../helpers/vi-html.helper';
import { ViAnalysisRequestApi } from '../../../api/visibilityIntelligence/models/vi-analysis-request.api.model';
import { AnswerEngineOption } from '../../models/answer-engine-option.model';
import { ViApiService } from '@api/visibilityIntelligence/vi.api.service';
import { ViBrandIdentity } from '../../models/vi-brand-identity.model';

type Brand = BrandIdentity | ViBrandIdentity;
@Component({
    selector: 'agpr-vi-insights-tab',
    templateUrl: './vi-insights-tab.component.html',
    styleUrls: ['./vi-insights-tab.component.scss'],
    providers: [
        {
            provide: TabComponent,
            useExisting: ViInsightTabComponent
        },
    ],
    standalone: false
})
export class ViInsightTabComponent extends TabComponent implements OnChanges {

    @Input() id: string = 'insightsTab';
    @Input() icon: string = 'fa-kit-duotone fa-agpr-insights';
    @Input() heading: string = 'Vi.qnA.tab.insights';
    @Input() tooltip: string = 'Vi.qnA.tab.insights';

    @Input() initialViBrand?: ViBrandIdentity;
    @Input() prompts: ViInsightPrompt[];
    @Input() answerEngineTypes: AnswerEngineOption[];
    @Input() executedPrompt: string;


    @Output() langSelectionChanged = new EventEmitter<string>();
    @Output() brandSelectionChanged = new EventEmitter<BrandIdentity>();

    selectedBrand: Brand | null;

    brandList$: Observable<BrandIdentity[]>;
    languageOptions$: Observable<DropdownOptionModel<string>[]>;

    customPrompt: string = '';

    selectedLanguageOption: DropdownOptionModel<string>;

    isInsightGenerating: boolean = false;

    private subscriptions: Subscription[] = [];

    readonly buttonType = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly brandListAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Common.dialog.aeoScout.insights.brandIdentity.autocomplete.focusBrand',
            action: (name: string) => {
                this.brandIdentityService.all$.pipe(
                    take(1),
                ).subscribe((brandList: BrandIdentity[]) => {
                    let brand: BrandIdentity | null = null;
                    const existingBrand = brandList.find(x => x.name === name.trim());
                    if (existingBrand) {
                        brand = existingBrand;
                    } else {
                        brand = new BrandIdentity();
                        brand.name = name;
                    }
                    this.selectedBrand = brand;
                });
            },
            appearCondition: AppearCondition.always,
            clearOnSelect: false,
            preferSearchkeyword: true
        },
        {
            label: 'Common.dialog.aeoScout.insights.brandIdentity.autocomplete.createBrand',
            action: (name: string) => {
                const brand = new BrandIdentity();
                brand.name = name;

                this.brandIdentityService.create(brand).subscribe(newBrand => {
                    this.selectedBrand = newBrand;
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    constructor(
        private brandIdentityService: BrandIdentityService,
        private currentUserStateService: CurrentUserStateService,
        private messageService: MessageService,
        private coreDialogService: CoreDialogService,
        private viApiService: ViApiService
    ) {
        super();

        this.brandList$ = this.brandIdentityService.all$;

        this.subscriptions.push(
            this.currentUserStateService.getDataReferenceLanguageTag().pipe(
                filter(x => !!x)
            ).subscribe(languageTag => {
                const mappedLangs = promptLanguageOptions.map<DropdownOptionModel<string>>(option => ({
                    ...option,
                    label: languageDataLocalizationMap[option.label]
                }));

                const initialLang = mappedLangs.find(x => {
                    // @HACK treat generic french code as french used in France
                    const lang = x.value === 'fr' ? 'fr-FR' : x.value;
                    return lang === languageTag;
                });

                this.selectedLanguageOption = initialLang ? initialLang : mappedLangs[0];
                this.languageOptions$ = of(mappedLangs);
            })
        );
    }


    ngOnChanges(changes: SimpleChanges) {
        if (changes['initialViBrand']) {
            this.selectedBrand = this.initialViBrand;
        }
    }

    languageChanged(option: DropdownOptionModel) {
        this.selectedLanguageOption = option;

        this.langSelectionChanged.emit(this.selectedLanguageOption?.value);
    }


    brandChanged(brand: BrandIdentity | string) {
        if (!brand) {
            this.selectedBrand = null;
        } else if (typeof brand === 'string') {
            if (brand.trim().length === 0) {
                this.selectedBrand = null;
                return;
            }

            this.selectedBrand = new BrandIdentity();
            this.selectedBrand.name = brand;

        } else {
            this.selectedBrand = brand;
        }

        this.brandSelectionChanged.emit(this.selectedBrand as BrandIdentity);
    }

    generateInsight(prompt: ViInsightPrompt, isCardClick: boolean) {
        if (isCardClick && (prompt.aiResponse || prompt.isCustomPrompt)) {
            return;
        }

        prompt.aiResponse = '';
        prompt.responseHtml = '';
        prompt.generating = true;
        prompt.brandContext = undefined;

        this.isInsightGenerating = true;

        prompt.destroyApiCall$ = new Subject<void>();

        prompt.apiCall = this.viApiService.getViAnalysis(this.constructAnalysisRequest(prompt)).pipe(
            takeUntil(prompt.destroyApiCall$),
            map((response) => this.extractAnalysisText(response)),
            catchError(e => {
                prompt.generating = false;
                prompt.destroyApiCall$ = null;
                throw e;
            })
        ).subscribe({
            next: (insight) => {
                prompt.brandName = this.selectedBrand?.name;
                prompt.brandContext = this.selectedBrand;

                if (prompt.isCustomPrompt) {
                    prompt.customPrompt = this.customPrompt;
                }

                prompt.aiResponse = insight;
                prompt.responseHtml = parseTextToHtml(insight, true);
                prompt.generating = false;
                prompt.language = this.selectedLanguageOption.value;
                this.isInsightGenerating = false;
            },
            error: () => {
                prompt.generating = false;
                this.isInsightGenerating = false;
            }
        });
    }


    cancelGeneration(prompt: ViInsightPrompt) {
        if (prompt.destroyApiCall$) {
            prompt.destroyApiCall$.next();
        }

        if (prompt.apiCall) {
            prompt.apiCall.unsubscribe();
        }

        prompt.generating = false;
        this.isInsightGenerating = false;
    }


    copyAnswer(event: Event, answer: string, removeBrTag = false) {
        if (!answer) {
            return;
        }

        const formattedText = htmlToFormattedText(answer, removeBrTag);
        copy.text(formattedText);

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Common.dialog.aeoScout.answerCopied.summary',
            detail: 'Common.dialog.aeoScout.answerCopied.detail',
        });

        (event.target as HTMLElement)?.focus();
    }

    openManageBrandsDialog() {
        this.coreDialogService.open(ManageBrandIdentitiesDialogComponent, {
            inputs: {
                overlayContainer: document.getElementById('main-content'),
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            },
            outputs: {}
        });
    }

    /**
     * @returns True when the prompt already reflects the current brand and language
     */
    matchesCurrentSettings(prompt: ViInsightPrompt): boolean {
        return this.isSameBrand(prompt)
            && prompt.language === this.selectedLanguageOption.value;
    }

    private isSameBrand(prompt: ViInsightPrompt): boolean {
        const brand = this.selectedBrand;

        if (!brand) {
            return false;
        }

        const id = this.getBrandId(brand);
        const promptBrandId = prompt.brandContext ? this.getBrandId(prompt.brandContext) : null;

        if (id) {
            return promptBrandId === id;
        }

        return !prompt.brandContext && prompt.brandName === brand.name;
    }

    /**
     * Resolve the brand's id, whichever shape it is (or null if it has none)
     */
    private getBrandId(brand: Brand): number | null {
        if ('brandId' in brand) {
            return brand.brandId ?? null;
        } else if ('id' in brand) {
            return brand.id ?? null;
        }

        return  null;
    }

    private extractAnalysisText(response: any): string {
        const content = response?.kwargs?.content;
        if (typeof content === 'string') {
            return content;
        }
        if (Array.isArray(content)) {
            return content.find((block: any) => block?.type === 'text')?.text ?? '';
        }
        return '';
    }

    private constructAnalysisRequest(prompt: ViInsightPrompt): ViAnalysisRequestApi {
        const answers = this.answerEngineTypes.filter(engineType => {
            const promptResonse = engineType?.rawResponse;
            const citations = promptResonse?.citations ?? [];
            const text = promptResonse?.text ?? '';

            return promptResonse && (citations.length > 0 || text.length > 0);
        }).map(engineOption => ({
            ...engineOption.rawResponse,
            citations: engineOption.rawResponse?.citations ?? [],
            text: engineOption.rawResponse?.text ?? ''
        }));

        return {
            originalQuestion: this.executedPrompt,
            answers,
            brandName: this.selectedBrand?.name ?? '',
            brandContext: (this.selectedBrand as ViBrandIdentity)?.toApiModel(),
            analysisType: prompt.analysisType,
            customPrompt: this.customPrompt,
            language: this.selectedLanguageOption.value
        };
    }
}
